import {Card, TabProps, TabsHeader} from 'platform/components';
import {Box} from 'platform/foundation';
import {match} from 'ts-pattern';

import {
  GetDirectSaleResponse,
  GetWarehousesResponse,
  HeaderDynamicActionsResponse,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {queryParams, Section} from '@dms/shared';

import {buildArray, RequiredTestIdProps, suffixTestId, useQueryState} from 'shared/web';

import type {EagGridMaximization} from 'features/eag-grid';

import {CheckoutTab} from './CheckoutTab';
import {DiscountsTab} from './DiscountsTab';
import {FastTrackIssuesTab} from './FastTrackIssuesTab';
import {RequestTab} from './RequestTab';
import {TABS_IDS} from './utils/tabsIds';

interface OverviewProps extends RequiredTestIdProps {
  maximization?: EagGridMaximization;
  directSale: GetDirectSaleResponse;
  directSaleActions?: HeaderDynamicActionsResponse['actions'];
  isDirectSaleLoading: boolean;
  isEditingDisabled: boolean;
  availableWarehouses: GetWarehousesResponse;
  onMaterialReturnModalOpen: VoidFunction;
}

export const Overview = (props: OverviewProps) => {
  const [activeTabId, setActiveTabId] = useQueryState(queryParams.COMPONENT_SECTIONS_TAB);

  const fastTrackIsCompleted = props.directSale.fastTrackIsCompleted;

  const tabs = buildArray<TabProps>()
    .add({
      id: TABS_IDS.requestTab,
      title: i18n.t('entity.warehouse.labels.requestTab'),
      content: (
        <Box height="93%">
          <RequestTab
            maximization={props.maximization}
            directSale={props.directSale}
            isDirectSaleLoading={props.isDirectSaleLoading}
            isEditingDisabled={props.isEditingDisabled}
            availableWarehouses={props.availableWarehouses}
            onMaterialReturnModalOpen={props.onMaterialReturnModalOpen}
            data-testid={suffixTestId('tabContent.request', props)}
          />
        </Box>
      ),
      'data-testid': suffixTestId('tab.request', props),
    })
    .add({
      id: TABS_IDS.fastTrackIssues,
      title: i18n.t('entity.warehouse.labels.fastTrackIssues'),
      icon: match(fastTrackIsCompleted)
        .with(true, () => 'success' as const)
        .with(false, () => 'pending' as const)
        .otherwise(() => undefined),
      content: (
        <Box height="93%">
          <FastTrackIssuesTab
            maximization={props.maximization}
            directSale={props.directSale}
            data-testid={suffixTestId('tabContent.fastTrackIssues', props)}
          />
        </Box>
      ),
      'data-testid': suffixTestId('tab.request', props),
    })
    .add({
      id: TABS_IDS.discountTab,
      title: i18n.t('entity.warehouse.labels.discountTab'),
      content: (
        <Card>
          <DiscountsTab
            directSale={props.directSale}
            isEditingDisabled={props.isEditingDisabled}
            data-testid={suffixTestId('tabContent.discount', props)}
          />
        </Card>
      ),
      'data-testid': suffixTestId('tab.discount', props),
    })
    .add({
      id: TABS_IDS.checkoutTab,
      title: i18n.t('entity.warehouse.labels.checkoutTab'),
      content: (
        <Card>
          <CheckoutTab
            directSale={props.directSale}
            isEditingDisabled={props.isEditingDisabled}
            data-testid={suffixTestId('tabContent.checkout', props)}
          />
        </Card>
      ),
      'data-testid': suffixTestId('tab.checkout', props),
    });

  const activeTabContent = tabs.find(
    (tab) => tab.id === (activeTabId ?? TABS_IDS.requestTab)
  )?.content;

  return (
    <Section isFullHeight data-testid={suffixTestId('wrapper', props)}>
      <TabsHeader
        variant="container"
        tabs={tabs}
        activeTabId={activeTabId}
        onChange={setActiveTabId}
        isFullWidth
      />
      {activeTabContent}
    </Section>
  );
};
